import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Header, PageWrapper } from "src/shared/components";
import { CURRENT_EVENT_SLUG } from "src/shared/constants/event";
import { LogoGradient } from "src/static/img";
import { useGetEventScheduleQuery } from "src/views/schedule/ScheduleContext/graphql/getEventSchedule.generated";

import DashboardResources from "./DashboardResources";
import { getDashboardSnapshot } from "./dashboardUtils";
import EventOverview from "./EventOverview";
import HackerHero from "./HackerHero";
import LumaCounter from "./LumaCounter";

const HackerHome: React.FC = () => {
  const [now, setNow] = useState(new Date());
  const { data, error, loading, refetch } = useGetEventScheduleQuery({
    variables: { eventSlug: CURRENT_EVENT_SLUG },
  });

  useEffect(() => {
    const clock = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(clock);
  }, []);

  useEffect(() => {
    const scheduleRefresh = window.setInterval(() => refetch(), 300000);
    return () => window.clearInterval(scheduleRefresh);
  }, [refetch]);

  const scheduleEvents = data?.eventSchedule ?? [];
  const { progress, lore, happeningNow, upNext } = getDashboardSnapshot(
    scheduleEvents,
    now
  );

  return (
    <>
      <DashboardHeader
        mobileLayout="compact"
        title="Home"
        subtitle="Your primary hub for schedule tracking, quick navigation, and activities."
        actionButton={<MobileLogo src={LogoGradient} alt="Hack the North" />}
      />
      <DashboardPageWrapper fullWidth stretchHeight={false}>
        <PageTitle>Home</PageTitle>
        <HackerHero lore={lore} now={now} />
        <DashboardContent>
          <OverviewGrid>
            <EventOverview
              happeningNow={happeningNow}
              upNext={upNext}
              now={now}
              isLoading={loading}
              hasError={Boolean(error)}
            />
            <LumaCounter now={now} progress={progress} />
          </OverviewGrid>
          <DashboardResources />
        </DashboardContent>
      </DashboardPageWrapper>
    </>
  );
};

export default HackerHome;

const DashboardHeader = styled(Header)`
  background: ${({ theme }) => theme.globalConstants.color.white};

  ${({ theme }) => theme.mediaQueries.tablet`
    background: ${theme.globalConstants.color.backgroundGray};
  `}
`;

const MobileLogo = styled.img`
  display: none;

  ${({ theme }) => theme.mediaQueries.tablet`
    display: block;
    width: 39px;
    height: 37px;
    object-fit: contain;
  `}
`;

const PageTitle = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const DashboardPageWrapper = styled(PageWrapper)`
  max-width: none;
  padding: 0;
  background: ${({ theme }) => theme.globalConstants.color.white};
`;

const DashboardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  width: 100%;
  padding: 40px;

  ${({ theme }) => theme.mediaQueries.tablet`
    gap: 32px;
    padding: 40px;
  `}

  ${({ theme }) => theme.mediaQueries.largeMobile`
    padding: 24px;
  `}

  ${({ theme }) => theme.mediaQueries.smallMobile`
    padding: 20px;
  `}
`;

const OverviewGrid = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  gap: 24px;
  min-width: 0;
  width: 100%;

  ${({ theme }) => theme.mediaQueries.medium`
    flex-direction: column;
  `}

  ${({ theme }) => theme.mediaQueries.tablet`
    gap: 32px;
  `}
`;
